import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GameInfo from '../components/checkout/GameInfo';
import ProductSelector from '../components/checkout/ProductSelector';
import OrderForm from '../components/checkout/OrderForm';
import SupportButton from '../components/SupportButton';
import { ProductDetail, ProductItem } from '../types/api';
import apiService from '../services/api';
import { analytics } from '../utils/analytics';

const Checkout: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('checkout');
  const [selectedProductItem, setSelectedProductItem] = useState<number | null>(null);

  const handleProductItemSelect = (itemId: number) => {
    setSelectedProductItem(itemId);
    if (productDetail) {
      const item = productItems.find(pi => pi.id === itemId);
      if (item) {
        analytics.selectItem(productDetail.id, item.id, item.name, item.price);
      }
    }
  };
  const [productDetail, setProductDetail] = useState<ProductDetail | null>(null);
  const [productItems, setProductItems] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (gameId) {
      fetchProductData(parseInt(gameId));
    }
  }, [gameId]);

  const fetchProductData = async (productId: number) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both product detail and items in parallel
      const [detailResponse, itemsResponse] = await Promise.all([
        apiService.getProductDetail(productId),
        apiService.getProductItems(productId),
      ]);

      setProductDetail(detailResponse.data);
      setProductItems(itemsResponse.data);
      analytics.viewGame(detailResponse.data.id, detailResponse.data.title);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('load_failed'));
      console.error('Error fetching product data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-6 md:py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !productDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-6 md:py-8">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error || t('product_not_found')}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-90"
            >
              {t('back_home')}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6 md:py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('back')}
        </button>

        {/* Mobile: Product Selection First, Desktop: 70/30 Layout */}
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Left Section (70%) - Game Info & Products - Shows first on mobile */}
          <div className="w-full lg:w-[70%] order-1 space-y-6">
            <GameInfo productDetail={productDetail} />
            <ProductSelector
              selectedProductItem={selectedProductItem}
              onProductItemSelect={handleProductItemSelect}
              productItems={productItems}
            />
          </div>

          {/* Right Section (30%) - Order Form - Shows second on mobile */}
          <div className="w-full lg:w-[30%] order-2">
            <OrderForm
              selectedProductItem={selectedProductItem}
              productDetail={productDetail}
              productItems={productItems}
            />
          </div>

        </div>
      </main>

      <Footer />
      <SupportButton />
    </div>
  );
};

export default Checkout;
